'use client';

import { FC, useState, useEffect } from 'react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useTeamContext } from '../../providers/TeamContext';
import Image from "next/image"

import { Player, Week, Match, PredictionMatchResult, Stage } from '@repo/types'; 

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { Minus, Plus, ChevronRight } from 'lucide-react'
import { Button } from "@/components/ui/button"
import { DriverCard } from "./driver-card"
import { DriverSelectModal } from "./driver-select-modal"

const moment = require('moment');
require('moment/locale/nl');
moment.locale('nl');

const PREDICTION_SLOTS = 3;


interface NewTeamProps {
  weekId: number;
}

const NewTeam: FC<NewTeamProps> = ({ weekId }) => {
  const { brand, competitionConfig, competitionData } = useCompetitionContext();
  const { team, removeDriverFromTeam, removeDriverFromPrediction, updateExtraPrediction, availableBudget, saveTeam, setTeamName, isSaving } = useTeamContext();
  
  const currentWeek = competitionData.weeks.find((week: Week) => week.weekId === weekId);
  const currentMatchType = competitionData.matches.find((match: Match) => match.weekId === weekId)?.type;

  const [teamNameLocal, setTeamNameLocal] = useState('');
  useEffect(() => {
    setTeamNameLocal(team.teamName);
  }, [team.teamName]);

  const handleCreateTeam = async () => {
    // setTeamName(teamNameLocal);
    saveTeam();
  };

  const drivers: Player[] = (competitionData.players || []).map((driver: any) => ({
    ...driver,
    club: competitionData.clubs.find((club: any) => club.id === driver.clubId)
  }));
  const qualRankingPositions = [...Array(20).keys()].map(i => `${i + 1}`);
  const raceRankingPositions = qualRankingPositions.concat(['DNF']);
  const selectionBlockSize = currentMatchType === 'sprint' ? 9 : 5;

  const [qualifyingPosition, setQualifyingPosition] = useState("1")
  const [racePosition, setRacePosition] = useState("1")
  const [qualifyingSprintPosition, setQualifyingSprintPosition] = useState("1")
  const [sprintPosition, setSprintPosition] = useState("1")

  const qualifyingPrediction: (PredictionMatchResult & { driver: Player })[] = team?.prediction.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
    if (currentPrediction.stage === 'qualifying') {
      const driver = drivers.find(driver => driver.id === currentPrediction.id);
      if (driver) {
        accumulator.push({ ...currentPrediction, driver });
      }
    }
    return accumulator;
  }, []) || [];
  const racePrediction: (PredictionMatchResult & { driver: Player })[] = team?.prediction.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
    if (currentPrediction.stage === 'race') {
      const driver = drivers.find(driver => driver.id === currentPrediction.id);
      if (driver) {
        accumulator.push({ ...currentPrediction, driver });
      }
    }
    return accumulator;
  }, []) || [];
  const qualifyingSprintPrediction: (PredictionMatchResult & { driver: Player })[] = team?.prediction.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
    if (currentPrediction.stage === 'qualifyingsprint') {
      const driver = drivers.find(driver => driver.id === currentPrediction.id);
      if (driver) {
        accumulator.push({ ...currentPrediction, driver });
      }
    }
    return accumulator;
  }, []) || [];
  const sprintPrediction: (PredictionMatchResult & { driver: Player })[] = team?.prediction.reduce<(PredictionMatchResult & { driver: Player })[]>((accumulator, currentPrediction) => {
    if (currentPrediction.stage === 'sprint') {
      const driver = drivers.find(driver => driver.id === currentPrediction.id);
      if (driver) {
        accumulator.push({ ...currentPrediction, driver });
      }
    }
    return accumulator;
  }, []) || [];

  const [agreeRules, setAgreeRules] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectionType, setSelectionType] = useState<'fantasyPick' | 'predictionPick'>('fantasyPick');
  const [selectionStage, setSelectionStage] = useState<Stage | undefined>(undefined);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const selectedDrivers = (team?.starting || []).map((driver: any) => ({
    ...driver,
    club: competitionData.clubs.find((club: any) => club.id === driver.clubId)
  }));;
  const emptySlotsCount = competitionConfig.lineupSize - selectedDrivers.length;
  const emptySlots = Array(emptySlotsCount).fill(null);

  const predictionSlotsArray = Array(PREDICTION_SLOTS).fill(null);
  const emptyQualSlotsCount = PREDICTION_SLOTS - qualifyingPrediction.length;
  const emptyRaceSlotsCount = PREDICTION_SLOTS - racePrediction.length;

  const handleRemoveDriver = (driver: Player, selectedType: 'fantasyPick' | 'predictionPick', stage: Stage | undefined) => {
    if(selectedType === 'fantasyPick') { 
      removeDriverFromTeam(driver) 
    } else if(stage) {
      removeDriverFromPrediction(driver, stage);
    }
  };

  const handleOpenModal = (selectedType: 'fantasyPick' | 'predictionPick', selectedIndex: number, stage?: Stage | undefined) => {
    setSelectionType(selectedType);
    setSelectionStage(stage);
    setSelectedIndex(selectedIndex);
    setIsModalOpen(true);
  };

  const domain = typeof window !== 'undefined' ? window.location.hostname : '';
  const baseDomain = domain.replace(/^www\./, '');
  const parts = domain.split('.')
  const url = parts.slice(-2).join('.')

  return (
    <>
      <div className="bg-gpspel-page p-4">

        {/* Deadline Banner */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="relative rounded-lg overflow-hidden h-20 flex items-center justify-center">
            <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern
                  id="stripe-pattern"
                  patternUnits="userSpaceOnUse"
                  width="20"
                  height="20"
                  patternTransform="rotate(45)"
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="20"
                    stroke="white"
                    strokeWidth="20"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#stripe-pattern)" />
            </svg>
            
            <h2 className="relative z-10 text-center text-black">
              Deadline: {moment.utc(currentWeek?.deadlineDate, "YYYY-MM-DD HH:mm:ss").local().format('dd DD/MM [om] HH.mm [uur]')}
            </h2>
          </div>

          <div 
            className="relative rounded-lg overflow-hidden bg-black"
            style={{ height: '80px' }}
          >
            <div className="absolute inset-0">
              <Image
                // src={domain.includes("nu.nl") ? `https://dpgnl.s3.eu-west-1.amazonaws.com/files/gps-boordradio-bg.jpg` : `https://dpgnl.s3.eu-west-1.amazonaws.com/files/gps-liveblog-bg.jpg`}
                src={`https://dpgnl.s3.eu-west-1.amazonaws.com/files/gps-liveblog-bg.jpg`}
                alt="Las Vegas GP"
                width={50}
                height={50}
                // className={`w-full h-full object-cover ${domain.includes("nu.nl") ? `object-top` : `object-center`} opacity-75`}
                className={`w-full h-full object-cover object-center opacity-75`}
              />
            </div>
              {/* <a href={domain.includes("nu.nl") ? `https://www.nu.nl/de-boordradio` : `https://www.${baseDomain}/formule-1/`} target='_blank' rel='noreferrer'> */}
              <a href={`https://www.${url}/formule-1/`} target='_blank' rel='noreferrer'>
                <div className="relative h-full flex items-center justify-between p-4 text-white">
                  <div className="space-y-1">
                  <div className="font-bold">Volg de GP van {competitionData.matches.filter((m: Match) => m.weekId === weekId)[0]?.feedUrl}</div>
                  <div className="text-sm">
                    {`en al het F1 nieuws op ${url}`}
                    {/* {domain.includes("nu.nl") ? `via de liveblog op De Boordradio` : 
                    `via de liveblog op ${baseDomain}`} */}
                  </div>
                  </div>
                  <div className="bg-white rounded-full p-2">
                    <ChevronRight className="w-5 h-5 text-black" />
                  </div>
                </div>
              </a>
          </div>
        </div>

        <div className="">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg`}>
            <div className="text-sm">1 van de {selectionBlockSize}</div>
            <h1 className="font-extra">
              Stel jouw team samen
            </h1>
          </div>
          <div className="bg-white rounded-b-lg pt-2">

            <div className="w-full max-w-md mx-auto border border-gpspel-grey-dark bg-gpspel-grey rounded-lg shadow-sm p-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="">Resterend budget:</span>
                <span className="font-extra">
                  {availableBudget.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
              {selectedDrivers.map((driver, index) => {
                // console.log(index, driver);
                return (
                  <DriverCard
                    key={`selected-${driver.id}`}
                    driver={driver}
                    weekId={weekId}
                    onRemove={() => handleRemoveDriver(driver, 'fantasyPick', undefined)}
                  />
                )})}
              {emptySlots.map((_, index) => (
                <DriverCard
                  key={`empty-${index}`}
                  weekId={weekId}
                  onSelect={() => handleOpenModal('fantasyPick', index)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sprint Prediction */}
        {currentMatchType === 'qualifyingsprint' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-pink-400`}>
              <div className="text-sm">2 van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Voorspel het podium voor de <span className='text-pink-400'>sprintkwalificatie</span>
              </h1>
            </div>
            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                {predictionSlotsArray.map((_, index) => {
                  const prediction = qualifyingSprintPrediction.find(pred => pred.index === index);
                  if (prediction) {
                    return (
                      <DriverCard
                        key={`sprint-selected-${prediction.driver.id}`}
                        driver={prediction.driver}
                        cardIndex={prediction.index}
                        weekId={weekId}
                        onRemove={() => handleRemoveDriver(prediction.driver, 'predictionPick', 'qualifyingsprint')}
                      />
                    );
                  } else {
                    return (
                      <DriverCard
                        key={`sprint-empty-${index}`}
                        weekId={weekId}
                        onSelect={() => handleOpenModal('predictionPick', index, 'qualifyingsprint')}
                      />
                    );
                  }
                })}
              </div>
            </div>
          </div>

          <div className="md:col-span-1">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-pink-400`}>
              <span className="text-sm">3 van de {selectionBlockSize}</span>
              <h2 className="font-extra">Waar eindigt Max Verstappen in de <span className='text-pink-400'>sprintkwalificatie</span>?</h2>
            </div>

            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = raceRankingPositions.indexOf(qualifyingSprintPosition);
                    const newIndex = (currentIndex - 1 + raceRankingPositions.length) % raceRankingPositions.length;
                    setQualifyingSprintPosition(raceRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('qualifyingsprint', raceRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <input
                  type="text"
                  value={qualifyingSprintPosition}
                  readOnly
                  className="w-12 text-center font-bold text-xl"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = raceRankingPositions.indexOf(qualifyingSprintPosition);
                    const newIndex = (currentIndex + 1) % raceRankingPositions.length;
                    setQualifyingSprintPosition(raceRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('qualifyingsprint', raceRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Sprint Prediction */}
        {currentMatchType === 'sprint' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-pink-400`}>
              <div className="text-sm">2 van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Voorspel het podium voor de <span className='text-pink-400'>sprintrace</span>
              </h1>
            </div>
            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
               {predictionSlotsArray.map((_, index) => {
                  const prediction = sprintPrediction.find(pred => pred.index === index);
                  if (prediction) {
                    return (
                      <DriverCard
                        key={`sprint-selected-${prediction.driver.id}`}
                        driver={prediction.driver}
                        cardIndex={prediction.index}
                        weekId={weekId}
                        onRemove={() => handleRemoveDriver(prediction.driver, 'predictionPick', 'sprint')}
                      />
                    );
                  } else {
                    return (
                      <DriverCard
                        key={`sprint-empty-${index}`}
                        weekId={weekId}
                        onSelect={() => handleOpenModal('predictionPick', index, 'sprint')}
                      />
                    );
                  }
                })}
              </div>
            </div>
          </div>

          <div className="md:col-span-1">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-pink-400`}>
              <span className="text-sm">3 van de {selectionBlockSize}</span>
              <h2 className="font-extra">Waar eindigt Max Verstappen in de <span className='text-pink-400'>sprint</span>?</h2>
            </div>

            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = raceRankingPositions.indexOf(sprintPosition);
                    const newIndex = (currentIndex - 1 + raceRankingPositions.length) % raceRankingPositions.length;
                    setSprintPosition(raceRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('sprint', raceRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <input
                  type="text"
                  value={sprintPosition}
                  readOnly
                  className="w-12 text-center font-bold text-xl"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = raceRankingPositions.indexOf(sprintPosition);
                    const newIndex = (currentIndex + 1) % raceRankingPositions.length;
                    setSprintPosition(raceRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('sprint', raceRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Qualifying Prediction */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-sky-300`}>
              <div className="text-sm">{selectionBlockSize === 9 ? 4 : 2} van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Voorspel het podium voor de <span className='text-sky-300'>kwalificatie</span>
              </h1>
            </div>
            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                {predictionSlotsArray.map((_, index) => {
                  const prediction = qualifyingPrediction.find(pred => pred.index === index);
                  if (prediction) {
                    return (
                      <DriverCard
                        key={`qual-selected-${prediction.driver.id}`}
                        driver={prediction.driver}
                        cardIndex={prediction.index}
                        weekId={weekId}
                        onRemove={() => handleRemoveDriver(prediction.driver, 'predictionPick', 'qualifying')}
                      />
                    );
                  } else {
                    return (
                      <DriverCard
                        key={`qual-empty-${index}`}
                        weekId={weekId}
                        onSelect={() => handleOpenModal('predictionPick', index, 'qualifying')}
                      />
                    );
                  }
                })}
              </div>
            </div>
          </div>

          <div className="md:col-span-1">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-sky-300`}>
          <span className="text-sm">{selectionBlockSize === 9 ? 5 : 3} van de {selectionBlockSize}</span>
              <h2 className="font-extra">Waar eindigt Max Verstappen in de <span className='text-sky-300'>kwalificatie</span></h2>
            </div>

            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = qualRankingPositions.indexOf(qualifyingPosition);
                    const newIndex = (currentIndex - 1 + qualRankingPositions.length) % qualRankingPositions.length;
                    setQualifyingPosition(qualRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('qualifying', qualRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <input
                  type="text"
                  value={qualifyingPosition}
                  readOnly
                  className="w-12 text-center font-bold text-xl"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = qualRankingPositions.indexOf(qualifyingPosition);
                    const newIndex = (currentIndex + 1) % qualRankingPositions.length;
                    setQualifyingPosition(qualRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('qualifying', qualRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Race Prediction */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-amber-400`}>
            <div className="text-sm">{selectionBlockSize === 9 ? 6 : 4} van de {selectionBlockSize}</div>
              <h1 className="font-extra">
                Voorspel het podium voor de <span className='text-amber-400'>race</span>
              </h1>
            </div>
            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                {predictionSlotsArray.map((_, index) => {
                  const prediction = racePrediction.find(pred => pred.index === index);
                  if (prediction) {
                    return (
                      <DriverCard
                        key={`race-selected-${prediction.driver.id}`}
                        driver={prediction.driver}
                        cardIndex={prediction.index}
                        weekId={weekId}
                        onRemove={() => handleRemoveDriver(prediction.driver, 'predictionPick', 'race')}
                      />
                    );
                  } else {
                    return (
                      <DriverCard
                        key={`race-empty-${index}`}
                        weekId={weekId}
                        onSelect={() => handleOpenModal('predictionPick', index, 'race')}
                      />
                    );
                  }
                })}
              </div>
            </div>
          </div>

          <div className="md:col-span-1">
          <div className={`bg-${brand}-header text-white p-4 rounded-t-lg border-b-4 border-b-amber-400`}>
            <span className="text-sm">{selectionBlockSize === 9 ? 9 : 5} van de {selectionBlockSize}</span>
              <h2 className="font-extra">Waar eindigt Max Verstappen in de <span className='text-amber-400'>race</span>?</h2>
            </div>

            <div className="bg-white rounded-b-lg pt-2">
              <div className="flex flex-wrap gap-6 items-center justify-center p-4 mb-8">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = raceRankingPositions.indexOf(racePosition);
                    const newIndex = (currentIndex - 1 + raceRankingPositions.length) % raceRankingPositions.length;
                    setRacePosition(raceRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('race', raceRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <input
                  type="text"
                  value={racePosition}
                  readOnly
                  className="w-12 text-center font-bold text-xl"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    const currentIndex = raceRankingPositions.indexOf(racePosition);
                    const newIndex = (currentIndex + 1) % raceRankingPositions.length;
                    setRacePosition(raceRankingPositions[newIndex] ?? "1");
                    updateExtraPrediction('race', raceRankingPositions[newIndex] ?? "1");
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md mx-auto border border-gpspel-grey-dark bg-gpspel-grey rounded-lg shadow-sm p-4 mb-4">
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="teamName">Teamnaam</Label>
                <Input 
                  id="teamName"
                  onChange={(e) => setTeamName(e.target.value)} 
                  type="text" 
                  value={teamNameLocal} 
                  placeholder="Kies een naam voor jouw F1 team" 
                />
              </div>
              <div className="mt-4 flex gap-3">
                <label htmlFor="spelregels" className="flex gap-3 cursor-pointer">
                  <div className="flex h-6 shrink-0 items-center">
                    <div className="group grid size-4 grid-cols-1">
                      <input
                        id="spelregels"
                        name="spelregels"
                        type="checkbox"
                        required
                        onChange={(e) => setAgreeRules(e.target.checked)}
                        aria-describedby="offers-description"
                        className={`col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-gray-600 checked:bg-gray-600 indeterminate:border-gray-600 indeterminate:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto`}
                      />
                      <svg
                        fill="none"
                        viewBox="0 0 14 14"
                        className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                      >
                        <path
                          d="M3 8L6 11L11 3.5"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="opacity-0 group-has-[:checked]:opacity-100"
                        />
                        <path
                          d="M3 7H11"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="opacity-0 group-has-[:indeterminate]:opacity-100"
                        />
                      </svg>
                    </div>
                  </div>
                  <span id="offers-description" className="text-sm/6 text-gray-500">
                    Ik ga akkoord met de{' '}
                    <a
                      href="/rules"
                      target="_blank"
                      className="font-medium text-gray-900"
                      onClick={(e) => e.stopPropagation()}
                    >
                      spelregels
                    </a>{' '}
                    <span className="text-red-600">*</span>
                  </span>
                </label>
              </div>
              <div className="flex gap-3">
                <label htmlFor="terms" className="flex gap-3 cursor-pointer">
                <div className="flex h-6 shrink-0 items-center">
                  <div className="group grid size-4 grid-cols-1">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      required={true}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      aria-describedby="offers-description"
                      className={`col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-gray-600 checked:bg-gray-600 indeterminate:border-gray-600 indeterminate:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto`}
                    />
                    <svg
                      fill="none"
                      viewBox="0 0 14 14"
                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                    >
                      <path
                        d="M3 8L6 11L11 3.5"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="opacity-0 group-has-[:checked]:opacity-100"
                      />
                      <path
                        d="M3 7H11"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="opacity-0 group-has-[:indeterminate]:opacity-100"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-sm/6">
                  {/* <label htmlFor="offers" className="font-medium text-gray-900">
                    Offers
                  </label>{' '} */}
                  <span id="offers-description" className="text-gray-500">
                    Ik ga akkoord met de <a href='/terms' target='_blank' className="font-medium text-gray-900">gebruikersvoorwaarden</a> <span className='text-red-600'>*</span>
                    {/* <span className="sr-only">Offers </span>when they are accepted or rejected by candidates. */}
                  </span>
                </div>
                </label>
              </div>
            </div>

        {/* Save Button */}
        <div className="flex justify-center m-8 ">
          <Button 
            onClick={handleCreateTeam}
            disabled={teamNameLocal === '' || emptySlotsCount > 0 || emptyQualSlotsCount > 0 || emptyRaceSlotsCount > 0 || !agreeRules || !agreeTerms 
              || isSaving // disable when saving
            } 
            variant="green"
            className="font-extrabold mb-8 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition duration-200"
            >
              {isSaving ? "Opslaan..." : "BEWAAR KEUZES"}
          </Button>
        </div>

      </div>
      <DriverSelectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        drivers={drivers}
        selectionType={selectionType}
        selectionStage={selectionStage}
        selectedIndex={selectedIndex}
      />
    </>
  )
}

export default NewTeam;
